import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import {
  CFCProps,
  Params,
  USERSROLE,
  VIEW_SUPPORT_TOOLS_LEFT_NAV,
  WebhookSubscriptionResponse,
  WebhookSubscriptionEventItem
} from 'types';
import { FullHeaderLeftSideFullPanelLayout } from 'layouts/fullHeader.LeftSideFullPanel.layout';
import { getWebhookSubscriptionsStart } from 'redux/webhook';
import EventPanel from './components/EventPanel';
import EventForm from './components/EventForm';
import { APP } from 'app/AppRoutes';
import {
  allUserEvents,
  aoAdminEvents,
  dbydAdminEvents,
  sysAdminEvents,
  getEventTitle,
  getEventHelpText
} from './helper';
import { addAlert } from 'redux/alerts/actions';
import Utils from 'utilities/helper.util';
import { useComponentMount } from 'utilities/hooks.util';

const WebHookSubscriptions: React.FC<Props> = ({
  personalList,
  allList,
  error,
  user,
  subscription
}) => {
  const dispatch = useDispatch();
  const [commonEvents, setCommonEvents] = useState<
    WebhookSubscriptionEventItem[]
  >(allUserEvents);
  const [allEvents, setAllEvents] = useState<WebhookSubscriptionEventItem[]>(
    []
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userRole, setUserRole] = useState<string>('');
  const [tenantId, setTenantId] = useState<number | undefined>();
  const [organisationId, setOrganisationId] = useState<number | undefined>();
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<
    WebhookSubscriptionEventItem | undefined
  >();
  const [selectedSubscription, setSelectedSubscription] = useState<
    WebhookSubscriptionResponse | undefined
  >();
  const [ready, setReady] = useState<boolean>(false);

  const fetchList = useCallback(() => {
    const queryParams: Params = {
      fields: '*'
    };
    queryParams && dispatch(getWebhookSubscriptionsStart(queryParams));
  }, [dispatch]);

  const onEventClick = (event: WebhookSubscriptionEventItem) => {
    setSelectedEvent(event);
    setIsFormOpen(true);
    if (event.id && Array.isArray(personalList) && Array.isArray(allList)) {
      const foundPersonal = personalList.find((s) => s.id === event.id);
      const foundAll = allList.find((s) => s.id === event.id);
      if (foundPersonal || foundAll) {
        setSelectedSubscription(foundPersonal || foundAll);
      }
    } else {
      setSelectedSubscription(undefined);
    }
  };

  useEffect(() => {
    user?.role && setUserRole(user.role);
    user?.Organisation?.id && setOrganisationId(user.Organisation.id);
    user?.Organisation?.tenantId && setTenantId(user.Organisation.tenantId);
  }, [user]);

  useEffect(() => {
    if (Array.isArray(personalList)) {
      const newList = allUserEvents.map(
        (e: WebhookSubscriptionEventItem): WebhookSubscriptionEventItem => {
          const found = personalList.find((s) => s.webhook === e.type);
          if (found) {
            return {
              ...e,
              status: found?.isPaused ? 'paused' : 'subscribed',
              id: found?.id
            };
          }

          return e;
        }
      );
      setIsLoading(false);
      setCommonEvents(newList);
    }
  }, [personalList]);

  useEffect(() => {
    if (Array.isArray(allList)) {
      let events: any[] = [];
      if (userRole === USERSROLE.AOADMIN) {
        events = aoAdminEvents;
      } else if (userRole === USERSROLE.DBYDADMIN) {
        if (tenantId) {
          events = dbydAdminEvents;
        } else {
          events = sysAdminEvents;
        }
      } else if (Utils.isValidUserRole(VIEW_SUPPORT_TOOLS_LEFT_NAV, userRole)) {
        events = sysAdminEvents;
      }

      const newList = events.map(
        (e: WebhookSubscriptionEventItem): WebhookSubscriptionEventItem => {
          const found = allList.find((s) => s.webhook === e.type);
          if (found) {
            return {
              ...e,
              status: found?.isPaused ? 'paused' : 'subscribed',
              id: found?.id,
              organisationId: organisationId
            };
          } else {
            return {
              ...e,
              organisationId: organisationId
            };
          }
        }
      );
      setIsLoading(false);
      setAllEvents(newList);
    }
  }, [allList, userRole, tenantId, organisationId]);

  useEffect(() => {
    if (error) {
      dispatch(addAlert('eventForm', 'error', error));
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (subscription) {
      if (typeof subscription !== 'string') {
        setSelectedSubscription(subscription);
      } else {
        setTimeout(() => {
          setIsFormOpen(false);
        }, 1500);
      }
      fetchList();
    }
  }, [subscription, fetchList]);

  useEffect(() => {
    ready && fetchList();
  }, [ready, fetchList, organisationId]);

  useComponentMount(() => {
    setReady(true);
  });

  return (
    <div
      id="WebHookSubscriptions"
      data-testid="WebHookSubscriptions"
      className="webhook-subscriptions"
    >
      <FullHeaderLeftSideFullPanelLayout
        pageHeading="Web hook subscriptions"
        pageDescription="Subscribe to web hook events and trigger your integrated solutions"
        enableHeaderGlobalBar={true}
      >
        <div className="webhook-subscriptions-container">
          <div className="webhook-subscriptions-instruction">
            Choose an event type to subscribe or modify …
          </div>
          <div className="webhook-subscriptions-events-container">
            <EventPanel
              isLoading={isLoading}
              title="Personal event types"
              helpText="Personal subscriptions affect only your enquiries"
              events={commonEvents}
              onEventClick={onEventClick}
            />
            {allEvents.length > 0 && (
              <EventPanel
                isLoading={isLoading}
                title={getEventTitle(userRole)}
                helpText={getEventHelpText(userRole)}
                events={allEvents}
                onEventClick={onEventClick}
              />
            )}
          </div>
          {isFormOpen &&
            ReactDOM.createPortal(
              <EventForm
                subscription={selectedSubscription}
                event={selectedEvent}
                handleCancel={() => setIsFormOpen(false)}
              />,
              document.getElementById(APP) || document.body
            )}
        </div>
      </FullHeaderLeftSideFullPanelLayout>
    </div>
  );
};

const mapStateToProps = ({ webhookCache, session }: RootState) => ({
  personalList: webhookCache?.subscriptions?.personalList,
  allList: webhookCache?.subscriptions?.allList,
  error: webhookCache?.subscriptions?.error,
  subscription: webhookCache?.subscriptions?.selected,
  user: session.currentUser
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

export default React.memo(connector(WebHookSubscriptions));
